'use client';

import type { ICollectionPipelineStatusV3 } from '@repo/sdk';
import { extractSubtitleV1Api } from '@repo/sdk';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { PipelineStepCell } from '../pipeline-step-cell';
import { PipelineStepCountBadge } from '../pipeline-step-count-badge';
import { PipelineStepDialog } from '../pipeline-step-dialog';
import { useStepAction } from '../use-step-action';
import { formatRanAt } from './format-ran-at';
import { StepLogPanel } from './step-log-panel';

interface IExtractStepProps {
  item: ICollectionPipelineStatusV3;
}

function resolveDisabled(item: ICollectionPipelineStatusV3): string | undefined {
  if (item.subtitleId === null) {
    return 'SRT not imported yet';
  }
  if (item.steps.matched.ranAt === null) {
    return 'Match not run yet';
  }
  return undefined;
}

export function ExtractStep({ item }: IExtractStepProps): JSX.Element {
  const { extracted } = item.steps;
  const subtitleId = item.subtitleId;
  const start = useCallback(
    (force: boolean) => {
      if (subtitleId === null) {
        throw new Error('SRT not imported yet');
      }
      return extractSubtitleV1Api({ subtitleId, force });
    },
    [subtitleId],
  );
  const action = useStepAction({ start, autoResume: true, initialForce: extracted.ranAt !== null });
  const disabled = resolveDisabled(item);

  return (
    <PipelineStepCell
      badge={<PipelineStepCountBadge step={extracted} />}
      renderDialog={(open, onOpenChange) => (
        <PipelineStepDialog
          open={open}
          onOpenChange={onOpenChange}
          title="AI Analysis"
          description={`Use AI to discover vocabulary the subtitle contains that isn't already in the challenge database. New challenges are created and linked to this collection. ${formatRanAt(extracted.ranAt)} · ${extracted.count} created.`}
          dataHref={`/sys/challenges?collectionId=${item.collectionId}&source=extract`}
          dataLabel="Open linked challenges"
          primaryAction={{
            label: extracted.ranAt === null ? 'Run AI analysis' : 'Re-run (force)',
            onClick: action.run,
            pending: action.status === 'running',
            disabledReason: disabled,
          }}
          body={<StepLogPanel status={action.status} log={action.log} />}
        />
      )}
    />
  );
}
